import { useEffect, useRef, useState } from "react";
import { api, errDetail, fmtDate } from "../lib/api";
import { useApp } from "../lib/store";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";
import {
  Mic, Square, Upload, Loader2, Keyboard, Trash2, Plus, ChevronDown, ChevronUp,
  AlertTriangle, ListChecks, Link2, Pencil, Sparkles, Bot, Check,
} from "lucide-react";

const RISK_STYLES = {
  green: { badge: "bg-emerald-100 text-emerald-700", border: "border-l-emerald-400", dot: "bg-emerald-500" },
  yellow: { badge: "bg-amber-100 text-amber-800", border: "border-l-amber-400", dot: "bg-amber-500" },
  orange: { badge: "bg-orange-100 text-orange-800", border: "border-l-orange-500", dot: "bg-orange-500" },
  red: { badge: "bg-red-100 text-red-700", border: "border-l-red-500", dot: "bg-red-500" },
};
const RISKS = ["green", "yellow", "orange", "red"];
const LANGS = [["auto", "vn_auto"], ["de", "Deutsch"], ["en", "English"], ["pt", "Português"], ["it", "Italiano"], ["zh", "中文"]];

const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

function RiskBadge({ level, t }) {
  const s = RISK_STYLES[level] || RISK_STYLES.green;
  return (
    <Badge className={`${s.badge} hover:${s.badge} gap-1.5`} data-testid={`risk-badge-${level}`}>
      <span className={`h-2 w-2 rounded-full ${s.dot}`} />{t(`vn_risk_${level}`)}
    </Badge>
  );
}

function TaskCreator({ note, users, currentUser, t, onCreated }) {
  const [rows, setRows] = useState(
    (note.suggested_tasks || []).map((s) => ({
      selected: true, subject: s.subject, due_date: plusDays(7),
      owner_id: currentUser.id, hint: s.due_hint || s.owner_hint || "",
    }))
  );
  const [saving, setSaving] = useState(false);
  if (!rows.length) return null;
  const upd = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const submit = async () => {
    const tasks = rows.filter((r) => r.selected && r.subject.trim()).map((r) => {
      const u = users.find((x) => x.id === r.owner_id);
      return { subject: r.subject, due_date: r.due_date, owner_id: r.owner_id, owner_name: u?.name || currentUser.name };
    });
    if (!tasks.length) return;
    setSaving(true);
    try {
      const res = await api.post(`/visit-notes/${note.id}/create-tasks`, { tasks });
      toast.success(`${t("vn_tasks_created")} (${res.data.created})`);
      onCreated();
    } catch (e) { toast.error(errDetail(e)); }
    setSaving(false);
  };

  return (
    <div className="mt-3 rounded-lg bg-amber-50/60 border border-amber-100 p-3" data-testid="task-creator">
      <p className="text-xs font-semibold text-gray-600 flex items-center gap-1.5 mb-2">
        <ListChecks className="h-3.5 w-3.5" />{t("vn_suggested_tasks")}
      </p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex flex-wrap items-center gap-2 bg-white rounded-md p-2 border border-gray-100">
            <Checkbox checked={r.selected} onCheckedChange={(v) => upd(i, "selected", !!v)} data-testid={`task-check-${i}`} />
            <Input className="h-8 text-xs flex-1 min-w-[160px]" value={r.subject}
              onChange={(e) => upd(i, "subject", e.target.value)} data-testid={`task-subject-${i}`} />
            <Input type="date" className="h-8 text-xs w-36" value={r.due_date}
              onChange={(e) => upd(i, "due_date", e.target.value)} data-testid={`task-due-${i}`} />
            <Select value={r.owner_id} onValueChange={(v) => upd(i, "owner_id", v)}>
              <SelectTrigger className="h-8 text-xs w-36" data-testid={`task-owner-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>
      <Button size="sm" className="mt-2 bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={submit} disabled={saving} data-testid="create-tasks-btn">
        {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}{t("vn_create_tasks")}
      </Button>
    </div>
  );
}

function NoteCard({ note, users, currentUser, canWrite, t, onChange }) {
  const [open, setOpen] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [override, setOverride] = useState(null);
  const s = RISK_STYLES[note.risk_level] || RISK_STYLES.green;
  const sum = note.summary || {};

  const saveOverride = async () => {
    if (!override.reason.trim()) { toast.error(t("vn_override_reason")); return; }
    try {
      await api.put(`/visit-notes/${note.id}`, { risk_level: override.level, risk_override_reason: override.reason });
      toast.success(t("decision_saved"));
      setOverride(null); onChange();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const del = async () => {
    try { await api.delete(`/visit-notes/${note.id}`); onChange(); } catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className={`bg-white border border-gray-200 border-l-4 ${s.border} rounded-lg p-4`} data-testid="visit-note-card">
      <div className="flex flex-wrap items-center gap-2">
        <RiskBadge level={note.risk_level} t={t} />
        <Badge variant="outline" className="gap-1 text-xs">{t(`vn_source_${note.source}`)}</Badge>
        {note.risk_overridden && <Badge className="bg-gray-100 text-gray-500 hover:bg-gray-100 text-[10px]">{t("vn_corrected")}</Badge>}
        <span className="text-xs text-gray-400 ml-auto">{note.owner_name} · {fmtDate(note.created_at)}</span>
        {canWrite && (
          <button className="text-gray-300 hover:text-red-500" onClick={del} data-testid="delete-note-btn"><Trash2 className="h-4 w-4" /></button>
        )}
      </div>

      <p className="text-sm text-gray-700 mt-2">
        <span className="font-medium">{sum.partner || "–"}</span>
      </p>
      <p className="text-xs text-gray-500 mt-1 flex items-start gap-1">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />{note.risk_reason}
      </p>
      {(note.risk_triggers || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5">
          {note.risk_triggers.map((tr, i) => <span key={i} className="text-[10px] bg-gray-100 text-gray-600 rounded px-1.5 py-0.5">{tr}</span>)}
        </div>
      )}

      <button className="text-xs text-blue-600 hover:underline mt-2 flex items-center gap-1" onClick={() => setOpen((o) => !o)} data-testid="expand-note-btn">
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}{t("info")}
      </button>

      {open && (
        <div className="mt-3 space-y-3 text-sm">
          {(sum.topics || []).length > 0 && (
            <Field label={t("vn_topics")}><ul className="list-disc pl-4 text-gray-600">{sum.topics.map((x, i) => <li key={i}>{x}</li>)}</ul></Field>
          )}
          {(sum.agreements || []).length > 0 && (
            <Field label={t("vn_agreements")}>
              <ul className="space-y-0.5 text-gray-600">{sum.agreements.map((a, i) => (
                <li key={i}>• <b>{a.who}</b>: {a.what}{a.due ? ` (${a.due})` : ""}</li>))}</ul>
            </Field>
          )}
          {(sum.competitors || []).length > 0 && (
            <Field label={t("vn_competitors")}><div className="flex flex-wrap gap-1">{sum.competitors.map((c, i) => <Badge key={i} className="bg-red-50 text-red-600 hover:bg-red-50">{c}</Badge>)}</div></Field>
          )}
          {sum.pricing && <Field label={t("vn_pricing")}><span className="text-gray-600">{sum.pricing}</span></Field>}
          {(sum.next_steps || []).length > 0 && (
            <Field label={t("vn_next_steps")}><ul className="list-disc pl-4 text-gray-600">{sum.next_steps.map((n, i) => <li key={i}>{n.task}{n.due_hint ? ` – ${n.due_hint}` : ""}</li>)}</ul></Field>
          )}
          {(note.linked_opportunities || []).length > 0 && (
            <Field label={t("vn_linked_opps")}><div className="flex flex-wrap gap-1">{note.linked_opportunities.map((o) => <Badge key={o.id} className="bg-blue-50 text-blue-700 hover:bg-blue-50 gap-1"><Link2 className="h-3 w-3" />{o.name}</Badge>)}</div></Field>
          )}
          <button className="text-xs text-gray-500 hover:underline" onClick={() => setShowTranscript((v) => !v)} data-testid="toggle-transcript-btn">
            {showTranscript ? t("vn_hide_transcript") : t("vn_show_transcript")}
          </button>
          {showTranscript && <p className="text-xs text-gray-500 whitespace-pre-wrap bg-gray-50 rounded p-2 border border-gray-100">{note.raw_transcript}</p>}

          {canWrite && (
            <div className="flex gap-2 pt-1">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOverride({ level: note.risk_level, reason: "" })} data-testid="override-risk-btn">
                <Pencil className="h-3 w-3 mr-1" />{t("vn_override")}
              </Button>
            </div>
          )}
          {canWrite && !note.tasks_created && <TaskCreator note={note} users={users} currentUser={currentUser} t={t} onCreated={onChange} />}
        </div>
      )}

      <Dialog open={!!override} onOpenChange={(o) => !o && setOverride(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("vn_override")}</DialogTitle>
            <DialogDescription>{t("vn_risk")}</DialogDescription></DialogHeader>
          {override && (
            <div className="space-y-3">
              <Select value={override.level} onValueChange={(v) => setOverride((o) => ({ ...o, level: v }))}>
                <SelectTrigger data-testid="override-level-select"><SelectValue /></SelectTrigger>
                <SelectContent>{RISKS.map((r) => <SelectItem key={r} value={r}>{t(`vn_risk_${r}`)}</SelectItem>)}</SelectContent>
              </Select>
              <div className="space-y-1">
                <Label className="text-xs">{t("vn_override_reason")}</Label>
                <Textarea rows={3} value={override.reason} onChange={(e) => setOverride((o) => ({ ...o, reason: e.target.value }))} data-testid="override-reason-input" />
              </div>
              <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={saveOverride} data-testid="override-save-btn">{t("save")}</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-gray-400 font-semibold mb-0.5">{label}</p>
      {children}
    </div>
  );
}

export function VisitNotes({ accountId }) {
  const { t, canWrite, user } = useApp();
  const [notes, setNotes] = useState([]);
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ source: "all", risk: "all", created_by: "all", date_from: "", date_to: "", q: "" });
  const [mode, setMode] = useState("idle"); // idle | recording | transcribing | review | analyzing
  const [draft, setDraft] = useState({ transcript: "", language: "auto", source: "voice" });
  const [aiSummary, setAiSummary] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileRef = useRef(null);

  const buildQuery = () => {
    const p = new URLSearchParams({ account_id: accountId });
    if (filters.source !== "all") p.set("source", filters.source);
    if (filters.risk !== "all") p.set("risk", filters.risk);
    if (filters.created_by !== "all") p.set("created_by", filters.created_by);
    if (filters.date_from) p.set("date_from", filters.date_from);
    if (filters.date_to) p.set("date_to", filters.date_to);
    if (filters.q) p.set("q", filters.q);
    return p.toString();
  };

  const load = () => api.get(`/visit-notes?${buildQuery()}`).then((r) => setNotes(r.data)).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [accountId, filters]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const startRec = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") { toast.error(t("vn_mic_error")); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => { stream.getTracks().forEach((tr) => tr.stop()); sendAudio(new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" })); };
      mediaRef.current = mr;
      mr.start();
      setMode("recording");
    } catch { toast.error(t("vn_mic_error")); }
  };

  const stopRec = () => { if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop(); setMode("transcribing"); };

  const sendAudio = async (blob) => {
    const fd = new FormData();
    const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
    fd.append("file", blob, `note.${ext}`);
    if (draft.language !== "auto") fd.append("language", draft.language);
    try {
      const r = await api.post("/visit-notes/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDraft((d) => ({ ...d, transcript: r.data.transcript, language: r.data.language || d.language, source: "voice" }));
      setMode("review");
    } catch (e) { toast.error(errDetail(e)); setMode("idle"); }
  };

  const aiSummarize = async () => {
    if (!draft.transcript.trim()) return;
    setAiBusy(true); setAiSummary(null);
    try {
      const r = await api.post("/ai-agents/run", { seed_key: "visit_report", input: draft.transcript });
      setAiSummary(r.data.output);
    } catch (e) { toast.error(errDetail(e)); }
    setAiBusy(false);
  };

  const analyzeSave = async () => {
    if (!draft.transcript.trim()) return;
    setMode("analyzing");
    try {
      await api.post("/visit-notes", { account_id: accountId, source: draft.source, raw_transcript: draft.transcript, language: draft.language === "auto" ? "" : draft.language });
      toast.success(t("vn_saved"));
      setDraft({ transcript: "", language: "auto", source: "voice" });
      setAiSummary(null);
      setMode("idle");
      load();
    } catch (e) { toast.error(errDetail(e)); setMode("review"); }
  };

  const uploadEmail = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    fd.append("account_id", accountId);
    setMode("analyzing");
    try {
      await api.post("/visit-notes/upload-email", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("vn_saved"));
      setMode("idle"); load();
    } catch (err) { toast.error(errDetail(err)); setMode("idle"); }
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="space-y-4" data-testid="visit-notes-panel">
      {canWrite && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          {(mode === "idle") && (
            <div className="flex flex-col items-center text-center gap-3 py-2">
              <Button onClick={startRec} data-testid="record-btn"
                className="h-20 w-20 rounded-full bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] shadow-lg shadow-amber-200 transition-transform hover:scale-105">
                <Mic className="h-8 w-8" strokeWidth={2} />
              </Button>
              <p className="text-sm text-gray-500">{t("vn_record_hint")}</p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Select value={draft.language} onValueChange={(v) => setDraft((d) => ({ ...d, language: v }))}>
                  <SelectTrigger className="h-8 w-40 text-xs" data-testid="lang-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGS.map(([v, l]) => <SelectItem key={v} value={v}>{l.startsWith("vn_") ? t(l) : l}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => { setDraft({ transcript: "", language: "auto", source: "manual" }); setMode("review"); }} data-testid="manual-entry-btn">
                  <Keyboard className="h-3.5 w-3.5 mr-1" />{t("vn_manual_entry")}
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => fileRef.current?.click()} data-testid="upload-email-btn">
                  <Upload className="h-3.5 w-3.5 mr-1" />{t("vn_upload_email")}
                </Button>
                <input ref={fileRef} type="file" accept=".eml,.msg,.pdf" className="hidden" onChange={uploadEmail} data-testid="email-file-input" />
              </div>
            </div>
          )}

          {mode === "recording" && (
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="relative">
                <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-60" />
                <div className="relative h-20 w-20 rounded-full bg-red-500 flex items-center justify-center text-white">
                  <Mic className="h-8 w-8" />
                </div>
              </div>
              <p className="text-sm font-medium text-red-500">{t("vn_recording")}</p>
              <Button onClick={stopRec} className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white rounded-full" data-testid="stop-btn">
                <Square className="h-4 w-4 mr-1" />{t("vn_stop")}
              </Button>
            </div>
          )}

          {(mode === "transcribing" || mode === "analyzing") && (
            <div className="flex flex-col items-center gap-2 py-6 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" />
              <p className="text-sm flex items-center gap-1">{mode === "analyzing" && <Sparkles className="h-3.5 w-3.5" />}{t(mode === "transcribing" ? "vn_transcribing" : "vn_analyzing")}</p>
            </div>
          )}

          {mode === "review" && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 flex items-center gap-1"><Pencil className="h-3.5 w-3.5" />{t("vn_edit_transcript")}</p>
              <Textarea rows={6} value={draft.transcript} placeholder={t("vn_manual_text_ph")}
                onChange={(e) => setDraft((d) => ({ ...d, transcript: e.target.value }))} data-testid="transcript-textarea" />

              <div className="flex items-center justify-between gap-2">
                <Button variant="outline" size="sm" className="h-8 text-xs border-[#1B2A4A]/30 text-[#1B2A4A]" onClick={aiSummarize}
                  disabled={!draft.transcript.trim() || aiBusy} data-testid="vn-ai-summarize-btn">
                  {aiBusy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Bot className="h-3.5 w-3.5 mr-1" />}{t("vn_ai_summarize")}
                </Button>
              </div>

              {aiSummary != null && (
                <div className="rounded-lg border border-[#1B2A4A]/15 bg-[#1B2A4A]/[0.03] p-3" data-testid="vn-ai-summary">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-[#1B2A4A] flex items-center gap-1"><Bot className="h-3.5 w-3.5 text-[#F5A623]" />{t("vn_ai_summary_title")}</span>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setDraft((d) => ({ ...d, transcript: aiSummary })); setAiSummary(null); }} data-testid="vn-ai-apply-btn">
                      <Check className="h-3.5 w-3.5 mr-1" />{t("vn_ai_apply")}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-600 whitespace-pre-wrap">{aiSummary}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2 justify-end">
                <Button variant="ghost" onClick={() => { setDraft({ transcript: "", language: "auto", source: "voice" }); setAiSummary(null); setMode("idle"); }} data-testid="cancel-draft-btn">{t("cancel")}</Button>
                <Button className="bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={analyzeSave} disabled={!draft.transcript.trim()} data-testid="analyze-save-btn">
                  <Sparkles className="h-4 w-4 mr-1" />{t("vn_analyze_save")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-center gap-2">
        <Input className="h-8 text-xs flex-1 min-w-[200px]" placeholder={t("vn_search_ph")} value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} data-testid="notes-search-input" />
        <Select value={filters.source} onValueChange={(v) => setFilters((f) => ({ ...f, source: v }))}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-source"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vn_filter_all_sources")}</SelectItem>
            {["voice", "email", "manual"].map((s) => <SelectItem key={s} value={s}>{t(`vn_source_${s}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.risk} onValueChange={(v) => setFilters((f) => ({ ...f, risk: v }))}>
          <SelectTrigger className="h-8 w-32 text-xs" data-testid="filter-risk"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vn_filter_all_risks")}</SelectItem>
            {RISKS.map((r) => <SelectItem key={r} value={r}>{t(`vn_risk_${r}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.created_by} onValueChange={(v) => setFilters((f) => ({ ...f, created_by: v }))}>
          <SelectTrigger className="h-8 w-36 text-xs" data-testid="filter-creator"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("vn_all_creators")}</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" className="h-8 w-32 text-xs" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="filter-from" />
        <Input type="date" className="h-8 w-32 text-xs" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="filter-to" />
      </div>

      {notes.length === 0 && <p className="text-sm text-gray-400 py-6 text-center">{t("vn_no_notes")}</p>}
      <div className="space-y-3">
        {notes.map((n) => (
          <NoteCard key={n.id} note={n} users={users} currentUser={user} canWrite={canWrite} t={t} onChange={load} />
        ))}
      </div>
    </div>
  );
}
